// src/contexts/AppContext.tsx
// Global state management för SireusCode

import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AppState, User, Notification, ContactFormData } from '../types';
import { translations } from '../locales';

// App Actions
export type AppAction = 
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' }
  | { type: 'SET_LANGUAGE'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'ADD_NOTIFICATION'; payload: Notification }
  | { type: 'REMOVE_NOTIFICATION'; payload: string }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'UPDATE_CONTACT_FORM'; payload: Partial<ContactFormData> }
  | { type: 'RESET_CONTACT_FORM' }
  | { type: 'TOGGLE_MOBILE_MENU'; payload?: boolean }
  | { type: 'SET_ONLINE_STATUS'; payload: boolean }
  | { type: 'SET_AUTHENTICATED'; payload: boolean };

// Initial state
const initialState: AppState = {
  user: null,
  theme: 'light',
  language: 'sv',
  loading: false,
  notifications: [],
  contactForm: {
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    preferredContact: 'email',
    gdprConsent: false
  },
  mobileMenuOpen: false,
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isAuthenticated: false
};

// App Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload,
        isAuthenticated: action.payload !== null
      };
      
    case 'SET_THEME':
      // Update document class for CSS styling
      if (typeof document !== 'undefined') {
        document.documentElement.className = action.payload;
      }
      return { ...state, theme: action.payload };
      
    case 'SET_LANGUAGE':
      // Update document lang attribute
      if (typeof document !== 'undefined') {
        document.documentElement.lang = action.payload;
      }
      return { ...state, language: action.payload };
      
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
      
    case 'ADD_NOTIFICATION':
      return { 
        ...state, 
        notifications: [...state.notifications, action.payload] 
      };
      
    case 'REMOVE_NOTIFICATION':
      return { 
        ...state, 
        notifications: state.notifications.filter(n => n.id !== action.payload) 
      };
      
    case 'CLEAR_NOTIFICATIONS':
      return { ...state, notifications: [] };
      
    case 'UPDATE_CONTACT_FORM':
      return { 
        ...state, 
        contactForm: { ...state.contactForm, ...action.payload } 
      };
      
    case 'RESET_CONTACT_FORM':
      return { 
        ...state, 
        contactForm: initialState.contactForm 
      };
      
    case 'TOGGLE_MOBILE_MENU':
      return { 
        ...state, 
        mobileMenuOpen: action.payload ?? !state.mobileMenuOpen 
      };
      
    case 'SET_ONLINE_STATUS':
      return { ...state, isOnline: action.payload };
      
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
      
    default:
      return state;
  }
};

// Context interface
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  // Helper functions
  t: (key: string, fallback?: string) => string;
  addNotification: (type: Notification['type'], message: string, autoClose?: boolean) => void;
  setLanguage: (lang: string) => void;
  toggleTheme: () => void;
  setUser: (user: User | null) => void;
  logout: () => void;
}

// Create Context
const AppContext = createContext<AppContextType | undefined>(undefined);

// Custom hook för att använda AppContext
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

// AppProvider Props
interface AppProviderProps {
  children: ReactNode;
}

// AppProvider-komponent
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Helper function för översättningar
  const t = (key: string, fallback?: string): string => {
    const translation = translations[state.language]?.[key];
    return translation || fallback || key;
  };

  // Helper function för notifikationer
  const addNotification = (
    type: Notification['type'], 
    message: string, 
    autoClose: boolean = true
  ) => {
    const notification: Notification = {
      id: `${Date.now()}-${Math.random()}`,
      type,
      message,
      timestamp: new Date(),
      autoClose
    };
    
    dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
    
    // Auto-remove efter 5 sekunder om autoClose är true
    if (autoClose) {
      setTimeout(() => {
        dispatch({ type: 'REMOVE_NOTIFICATION', payload: notification.id });
      }, 5000);
    }
  };

  // Helper function för språkbyte
  const setLanguage = (lang: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sireus-language', lang);
    }
    dispatch({ type: 'SET_LANGUAGE', payload: lang });
  };

  // Helper function för tema-byte
  const toggleTheme = () => {
    const newTheme = state.theme === 'light' ? 'dark' : 'light';
    if (typeof window !== 'undefined') {
      localStorage.setItem('sireus-theme', newTheme);
    }
    dispatch({ type: 'SET_THEME', payload: newTheme });
  };

  // Helper function för användarhantering
  const setUser = (user: User | null) => {
    dispatch({ type: 'SET_USER', payload: user });
  };

  // Helper function för utloggning
  const logout = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('sireus-token');
      localStorage.removeItem('sireus-refresh-token');
    }
    dispatch({ type: 'SET_USER', payload: null });
    addNotification('info', t('logoutSuccess', 'Du har loggats ut'));
  };

  // Effekt för att ladda sparade inställningar
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const savedLanguage = localStorage.getItem('sireus-language');
    const savedTheme = localStorage.getItem('sireus-theme') as 'light' | 'dark';
    const savedToken = localStorage.getItem('sireus-token');
    
    if (savedLanguage && savedLanguage !== state.language) {
      dispatch({ type: 'SET_LANGUAGE', payload: savedLanguage });
    }
    
    if (savedTheme && savedTheme !== state.theme) {
      dispatch({ type: 'SET_THEME', payload: savedTheme });
    }
    
    if (savedToken) {
      // Här skulle vi validera token mot backend
      dispatch({ type: 'SET_AUTHENTICATED', payload: true });
    }
    // Ska bara köras en gång vid mount för att läsa sparade inställningar
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Effekt för att lyssna på online/offline status
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => {
      dispatch({ type: 'SET_ONLINE_STATUS', payload: true });
      addNotification('success', t('backOnline', 'Anslutningen är återställd'));
    };
    
    const handleOffline = () => {
      dispatch({ type: 'SET_ONLINE_STATUS', payload: false });
      addNotification('warning', t('offlineMode', 'Du är offline'), false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // 't' återskapas varje render; inkludera den skulle lägga till/ta bort lyssnarna vid varje render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.language]);

  // Context value
  const contextValue: AppContextType = {
    state,
    dispatch,
    t,
    addNotification,
    setLanguage,
    toggleTheme,
    setUser,
    logout
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};